const H1_REGEX = /^# (.+)$/;
const H2_REGEX = /^## (.+)$/;
const UL_REGEX = /^- (.+)$/;
const OL_REGEX = /^\d+\. (.+)$/;

const escapeHtml = (text: string): string => {
	const map: Record<string, string> = {
		'&': '&amp;',
		'<': '&lt;',
		'>': '&gt;',
		'"': '&quot;',
		"'": '&#039;',
	};
	return text.replace(/[&<>"']/g, (char) => map[char]);
};

const unescapeHtml = (text: string): string => {
	const map: Record<string, string> = {
		'&amp;': '&',
		'&lt;': '<',
		'&gt;': '>',
		'&quot;': '"',
		'&#039;': "'",
		'&nbsp;': ' ',
	};
	return text.replace(/&[^;]+;/g, (entity) => map[entity] || entity);
};

const processInlineFormatting = (text: string): string => {
	let result = escapeHtml(text);
	result = result.replace(/\*\*(.+?)\*\*/g, '<b>$1</b>');
	result = result.replace(/\*(.+?)\*/g, '<i>$1</i>');
	return result;
};

interface ListState {
	inList: boolean;
	listType: 'ul' | 'ol' | null;
}

const closeListIfNeeded = (result: string[], state: ListState) => {
	if (state.inList) {
		result.push(state.listType === 'ul' ? '</ul>' : '</ol>');
		state.inList = false;
		state.listType = null;
	}
};

const handleHeader = (line: string, result: string[], state: ListState): boolean => {
	const h1Match = H1_REGEX.exec(line);
	if (h1Match) {
		closeListIfNeeded(result, state);
		const content = escapeHtml(h1Match[1]);
		result.push(`<h2>${content}</h2>`);
		return true;
	}

	const h2Match = H2_REGEX.exec(line);
	if (h2Match) {
		closeListIfNeeded(result, state);
		const content = escapeHtml(h2Match[1]);
		result.push(`<h3>${content}</h3>`);
		return true;
	}
	return false;
};

const handleList = (line: string, result: string[], state: ListState): boolean => {
	const ulMatch = UL_REGEX.exec(line);
	if (ulMatch) {
		const content = processInlineFormatting(ulMatch[1]);
		if (!state.inList || state.listType !== 'ul') {
			if (state.inList && state.listType === 'ol') result.push('</ol>');
			result.push('<ul>');
			state.inList = true;
			state.listType = 'ul';
		}
		result.push(`<li>${content}</li>`);
		return true;
	}

	const olMatch = OL_REGEX.exec(line);
	if (olMatch) {
		const content = processInlineFormatting(olMatch[1]);
		if (!state.inList || state.listType !== 'ol') {
			if (state.inList && state.listType === 'ul') result.push('</ul>');
			result.push('<ol>');
			state.inList = true;
			state.listType = 'ol';
		}
		result.push(`<li>${content}</li>`);
		return true;
	}
	return false;
};

export const markdownToHtml = (markdown: string): string => {
	if (!markdown) return '<p><br></p>';

	const lines = markdown.split('\n');
	const result: string[] = [];
	const state: ListState = { inList: false, listType: null };

	for (const line of lines) {
		const trimmedLine = line.trim();

		if (handleHeader(line, result, state)) continue;
		if (handleList(line, result, state)) continue;

		if (trimmedLine === '') {
			closeListIfNeeded(result, state);
			continue;
		}

		closeListIfNeeded(result, state);
		const processed = processInlineFormatting(line);
		result.push(`<p>${processed}</p>`);
	}

	closeListIfNeeded(result, state);
	return result.join('');
};

export const htmlToMarkdown = (html: string): string => {
	if (!html) return '';

	let markdown = html;

	markdown = markdown.replace(/<div[^>]*>/gi, '\n');
	markdown = markdown.replace(/<\/div>/gi, '');
	markdown = markdown.replace(/<br\s*\/?>/gi, '\n');

	markdown = markdown.replace(/<h1[^>]*>(.*?)<\/h1>/gi, '\n\n# $1\n\n');
	markdown = markdown.replace(/<h2[^>]*>(.*?)<\/h2>/gi, '\n\n# $1\n\n');
	markdown = markdown.replace(/<h3[^>]*>(.*?)<\/h3>/gi, '\n\n## $1\n\n');

	markdown = markdown.replace(/<strong[^>]*>(.*?)<\/strong>/gi, '**$1**');
	markdown = markdown.replace(/<b[^>]*>(.*?)<\/b>/gi, '**$1**');
	markdown = markdown.replace(/<em[^>]*>(.*?)<\/em>/gi, '*$1*');
	markdown = markdown.replace(/<i[^>]*>(.*?)<\/i>/gi, '*$1*');

	markdown = markdown.replace(/<ul[^>]*>([\s\S]*?)<\/ul>/gi, (_match, content) => {
		return '\n' + content.replace(/<li[^>]*>(.*?)<\/li>/gi, (_m: string, item: string) => {
			return `- ${item.trim()}\n`;
		}) + '\n';
	});

	markdown = markdown.replace(/<ol[^>]*>([\s\S]*?)<\/ol>/gi, (_match, content) => {
		let counter = 1;
		return '\n' + content.replace(/<li[^>]*>(.*?)<\/li>/gi, (_m: string, item: string) => {
			return `${counter++}. ${item.trim()}\n`;
		}) + '\n';
	});

	markdown = markdown.replace(/<p[^>]*>(.*?)<\/p>/gi, '$1\n\n');

	markdown = markdown.replace(/<[^>]+>/g, '');
	markdown = unescapeHtml(markdown);

	return markdown
		.split('\n')
		.map((line) => line.trim())
		.filter((line, index, arr) => {
			if (line === '' && arr[index - 1] === '') return false;
			return true;
		})
		.join('\n')
		.trim();
};
